import { User } from "lucide-react";
import type * as React from "react";
import { ActionCell, type ActionItem } from "@/components/shared/action-cell";
import {
  EntityCardContent,
  EntityCardsGrid,
  entityCardClass,
} from "@/components/shared/entity-cards";
import type { User as UserRow } from "@/hooks/use-users";
import { AppRoleBadge } from "./AppRoleBadge";
import { LinkedPerson } from "./LinkedPerson";

interface UserCardsProps {
  users: UserRow[];
  loading?: boolean;
  empty: React.ReactNode;
  /** Builds the per-user action menu — same list the table row uses. */
  buildActions: (user: UserRow) => ActionItem[];
  /** The signed-in user's id; their own card's menu is disabled (as in the table). */
  currentUserId?: string;
}

/**
 * Card view for the admin users directory. Unlike the capability cards these
 * are NOT links — users have no detail page — so each card carries the same
 * per-row action menu the table uses.
 */
export function UserCards({ users, loading, empty, buildActions, currentUserId }: UserCardsProps) {
  return (
    <EntityCardsGrid loading={loading} isEmpty={users.length === 0} empty={empty}>
      {users.map((user) => (
        <div key={user.id} className={entityCardClass(false)}>
          <EntityCardContent
            icon={User}
            title={user.name}
            subtitle={user.email}
            footer={
              <>
                <AppRoleBadge role={user.role} />
                {user.banned && (
                  <span
                    className="inline-flex items-center rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive"
                    title={user.banReason ?? "Banned"}
                  >
                    Banned
                  </span>
                )}
                <LinkedPerson user={user} />
              </>
            }
            trailing={
              <ActionCell
                items={buildActions(user)}
                disabled={user.id === currentUserId}
                triggerLabel={`Actions for ${user.name}`}
              />
            }
          />
        </div>
      ))}
    </EntityCardsGrid>
  );
}
